/*
 * 登录接口。
 *
 * 这一层只搬数据，不做判断：能不能显示微信登录按钮由 `LoginPage` 结合
 * 宿主能力与服务端下发的 `loginMethods` 决定，不在这里写死。
 *
 * 不变量：
 *   - **不接触 Token。** 凭据由服务端写进 httpOnly Cookie，前端读不到也不需要读。
 *     旧项目的 `localStorage.setItem('Authorization', ...)` 在这里不存在对应物。
 *   - 不声明 characterId / productKey / formKey，那些由服务端从 Session 解析。
 */

import type { CharacterAppearanceView } from "@dreamverse/contracts";
import { apiRequest } from "./api-client";
import { apiHostKind } from "./host-bridge";

/** 服务端支持哪些登录方式。与宿主能力求交集后才是实际可用的方式。 */
export interface LoginMethods {
  phone: boolean;
  wechatMiniapp: boolean;
  wechatOfficial: boolean;
  /** 项目唯一环境模式，由服务端权威下发。 */
  environment: "development" | "production";
  /**
   * 按 userId 冒充这条路**从当前这台机器打得通吗**。
   *
   * ⚠ **不能只看 `environment`。** development 免密对所有来源都生效，
   * 而冒充那条路由额外要求请求来自服务端本机。所以远程访问一台开着免密的
   * 开发服务器时环境是 development 而这个仍可能为假——警告该给还是要给，
   * 但那个折叠框不画，画了也是 403。
   */
  devImpersonation: boolean;
}

/**
 * 入口维护中时服务端的业务码。
 *
 * ⚠ 与 `apps/api/src/modules/identity/app-session.service.ts` 的
 * `PRODUCT_MAINTENANCE_CODE` **手工镜像**——`packages/contracts` 里没有
 * 会话相关的类型，`LoginMethods` / `SessionBootstrap` 也都是这么镜像的。
 * 改一边要改两边。
 *
 * `GET session` 不会用它（那条路由回 200 + `status: "maintenance"`），
 * 其余 `/api/v1/app/*` 在维护中一律 503 带这个码。
 */
export const PRODUCT_MAINTENANCE_CODE = "PRODUCT_MAINTENANCE";

/** `GET /api/v1/app/session` 的响应。未登录时返回 200 + anonymous，不是 401。 */
export interface SessionBootstrap {
  /**
   * ⚠ `maintenance` 与登录状态**正交**：登录的和没登录的都可能拿到它。
   * 拿到就整屏切维护页，别的字段一概不看。
   *
   * 它走 200 而不是 503，因为首屏只发这一次请求就得知道该画什么。
   * 其余接口在维护中一律 503 + `PRODUCT_MAINTENANCE`。
   */
  status: "anonymous" | "authenticated" | "needs_profile" | "blocked" | "maintenance";
  /**
   * 运营在管理端填的维护说明。没填就没有这个键，
   * **维护页有自己写死的兜底句**——API 整个挂掉时这一条也拿不到。
   */
  maintenanceNote?: string;
  /** 当前入口的默认角色名。维护页名牌使用；没有配置角色时不下发。 */
  characterDisplayName?: string;
  appearance?: CharacterAppearanceView;

  /** 当前会话 ID。发消息、补拉 sequence 都以它为键。 */
  conversationId?: string;
  displayName?: string;
  /**
   * **用户自己的**头像。没有就不下发这个键。
   *
   * ⚠ 与 `appearance.avatarUrl` 是两回事，后者是角色的。顶层这两个键描述账号，
   * `appearance` 里的同名键描述角色——`session.tsx` 把顶层这两个重命名成
   * `accountDisplayName` / `accountAvatarUrl` 就是为了消掉这个歧义，
   * 别在别处直接摊平使用。
   */
  avatarUrl?: string;
  /** 当前入口首页是否进入独立资料查询；保留旧字段名，由服务端按权威宿主与羁绊裁定。 */
  knowledgeQaHome?: boolean;
  loginMethods: LoginMethods;
}

export interface SmsCodeResult {
  environment: "development" | "production";
  /** 免密时的占位码，用于自动回填；非免密恒为 null。 */
  devCode: string | null;
  /** 距离可以重新发送还有多少秒。 */
  cooldownSeconds: number;
}

/**
 * 首屏引导。
 *
 * 上报 `hostKind` 是为了让**未登录**的访客也能拿到正确的入口状态：
 * 那时候没有 Session，服务端无从得知这是小程序壳还是浏览器，
 * 不给的话它只能从域名和兜底入口猜。登录之后这个参数不再起作用，
 * 入口以 `AuthSession.productId` 为准。
 */
export function fetchSession(signal?: AbortSignal): Promise<SessionBootstrap> {
  return apiRequest<SessionBootstrap>(`/api/v1/app/session?hostKind=${apiHostKind()}`, {
    ...(signal ? { signal } : {}),
  });
}


export function requestSmsCode(phone: string): Promise<SmsCodeResult> {
  return apiRequest<SmsCodeResult>("/api/v1/app/auth/sms/code", { body: { phone } });
}

export function loginByPhone(phone: string, code: string): Promise<SessionBootstrap> {
  return apiRequest<SessionBootstrap>("/api/v1/app/auth/phone", {
    body: { phone, code, hostKind: apiHostKind() },
  });
}

/**
 * 小程序壳登录。
 *
 * code 来自壳调用的 `wx.login`，H5 只负责透传，不解析也不存储。
 * hostKind 不用上报——能拿到小程序 code 本身就证明了宿主。
 */
export function loginByMiniappCode(code: string): Promise<SessionBootstrap> {
  return apiRequest<SessionBootstrap>("/api/v1/app/auth/wechat/miniapp", { body: { code } });
}

/**
 * 开发期冒充登录：按 userId 直接拿到会话。
 *
 * **不是登录方式，是测试入口。** 服务端两道闸——非 development、
 * 非本机来源——任一成立就 404 / 403。
 * 判断能不能画入口只看 `loginMethods.devImpersonation`（那个字段已经把两道闸
 * 算过一遍了），不要在这里自己推断环境。
 *
 * 库里 98% 的迁移账号只有微信 openid 没有手机号，短信那条路登不进他们，
 * 而输一个没注册的手机号会**新建**账号。这条路查不到就 404，绝不建号。
 */
export function loginAsUser(userId: string): Promise<SessionBootstrap> {
  return apiRequest<SessionBootstrap>("/api/v1/app/auth/dev", {
    body: { userId, hostKind: apiHostKind() },
  });
}

export function logout(): Promise<{ status: "anonymous"; loginMethods: LoginMethods }> {
  return apiRequest("/api/v1/app/auth/logout", { method: "POST", body: {} });
}

/**
 * 服务号网页授权的入口地址。
 *
 * 这是一个整页跳转，不是 fetch：微信授权页必须在顶层窗口打开。
 * 服务端会 302 到微信，授权后再 302 回来，凭据全程只走 Cookie，
 * **不经过 URL**。
 */
export function wechatOfficialLoginUrl(redirect: string): string {
  return `/api/v1/app/auth/wechat/official/start?redirect=${encodeURIComponent(redirect)}`;
}

/* -------------------------------------------------------------------------- */
/* 登录设备                                                                    */
/* -------------------------------------------------------------------------- */

/*
 * 这里曾经有 `DeviceSession` / `fetchSessions` / `revokeSession`，
 * 对应设置页的「登录设备」一节。**界面于 2026-08-01 按仓库主人要求撤掉了**，
 * 客户端这一侧随之删除，不留没有调用方的导出。
 *
 * 后端 `GET /api/v1/app/auth/sessions` 与 `DELETE .../:id` **仍然在**，
 * 也仍然是 `AuthSession` 可撤销这个设计唯一的对外出口。要把这一节接回来时
 * 从那两条路由重新写客户端即可；⚠ 文案必须说「最多再过 15 分钟」而不是
 * 「立即下线」——撤销的是 Refresh Session，已发出的 Access Token
 * 在剩余有效期内照样能用。
 */
